import React, { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { HiEllipsisVertical } from "react-icons/hi2";

import SelectUser from "../../../components/selectUser";
import DebounceInput from "../../../components/DebounceInput";
import Pagination from "../../../components/Paginator";
import MultiSelect from "../../../components/MultiSelect";
import useStore from "../../../services/store";

import api from "../../../services/api";
import { ORG_IDS } from "@/config/organisations";
import CreateProject from "./createProject";

const addRecentProject = (project) => {
  const max = 10;
  let recent = JSON.parse(localStorage.getItem("recentProjects") || "[]");
  recent = recent.filter((p) => p._id !== project._id);
  recent.unshift({ _id: project._id, name: project.name, logo: project.logo });
  if (recent.length > max) recent = recent.slice(0, max);
  localStorage.setItem("recentProjects", JSON.stringify(recent));
};

export default function List() {
  const [filters, setFilters] = useState({
    status: "active",
    search: "",
    lead_id: "",
    account_manager_id: "",
    responsible_id: "",
    stage: "",
    page: 1,
    per_page: 10,
  });
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState();
  const [budgetsByProject, setBudgetsByProject] = useState({});

  const user = useSelector((state) => state.Auth.user);
  const { businessUnit } = useSelector((state) => state.Auth);
  const navigate = useNavigate();
  const organisation = useStore((state) => state.organisation);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const query = { types: ["project"] };
        if (filters.search) query.search = filters.search;
        if (filters.lead_id) query.lead_id = filters.lead_id;
        if (filters.account_manager_id) query.account_manager_id = filters.account_manager_id;
        if (filters.responsible_id) query.responsible_id = filters.responsible_id;
        if (filters.status) query.status = filters.status;
        if (filters.stage && (!Array.isArray(filters.stage) || filters.stage.length > 0)) query.stage = filters.stage;

        // BU filter
        if (businessUnit._id !== "ALL") query.business_unit_id = businessUnit._id;
        if (user.role === "client") query.client_id = user._id;

        // Filter by organisation if selected (and not "ALL")
        if (organisation && organisation._id !== "ALL") {
          // Le Collectif organisation_id (legacy)
          const LE_COLLECTIF_ORG_ID = ORG_IDS.LE_COLLECTIF;
          
          // Check if this is Selego by slug (most reliable) or name
          const orgSlug = organisation.slug?.toLowerCase();
          const orgName = organisation.name?.toLowerCase() || "";
          const isSelego = orgSlug === "selego" || orgName.includes("selego");
          
          if (isSelego) {
            // For Selego, include: Selego org, Le Collectif org, and legacy projects without org_id
            // Also check display_organisation_ids for projects visible in Selego
            query.$or = [
              { organisation_id: organisation._id },
              { display_organisation_ids: organisation._id },
              { organisation_id: LE_COLLECTIF_ORG_ID },
              { display_organisation_ids: LE_COLLECTIF_ORG_ID },
              { organisation_id: { $exists: false } },
              { organisation_id: null }
            ];
          } else {
            // For other orgs (Waalego, Tirana), filter by their organisation_id
            // Backend will check both organisation_id (primary) and display_organisation_ids (visibility)
            query.organisation_id = organisation._id;
          }
        }

        query.page = filters.page;
        query.per_page = filters.per_page;

        const { ok, data } = await api.post("/project/search", query);
        if (!ok) throw new Error("Failed to fetch projects");
        setProjects(data.projects);
        setTotal(data.total);

        const projectIds = data.projects.map((p) => p._id);
        if (projectIds.length === 0) {
          setBudgetsByProject({});
          return;
        }
        const budgetsRes = await api.post("/budget/search", { projectIds, status: "active", visibility: "public" });
        if (!budgetsRes.ok) throw new Error("Failed to fetch budgets");
        const grouped = {};
        for (const b of budgetsRes.data) {
          if (!grouped[b.projectId]) grouped[b.projectId] = [];
          grouped[b.projectId].push(b);
        }
        setBudgetsByProject(grouped);
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch projects");
      }
    };
    fetchData();
  }, [filters, organisation, businessUnit._id]);

  return (
    <div className="py-8 px-10">
      <RecentProjects
        onSelect={(project) => {
          addRecentProject(project);
          navigate(`/project/${project._id}`);
        }}
      />
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{`${total === undefined ? `Loading...` : `${total} projects`} `}</h1>
        <div className="flex items-center gap-4">
          <PerPageSelector value={filters.per_page} onChange={(per_page) => setFilters({ ...filters, per_page, page: 1 })} />
          <CreateProject />
        </div>
      </div>
      <section>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="w-1/5 space-y-1">
            <DebounceInput
              debounce={300}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, page: 1, search: e.target.value })}
              className="input"
              placeholder="Search"
            />
          </div>
          <div className="w-1/5 space-y-1">
            <SelectUser
              placeholder="Lead"
              value={{ _id: filters.lead_id, name: filters.lead_name, avatar: filters.lead_avatar }}
              onChange={(user) => setFilters({ ...filters, page: 1, lead_id: user?._id, lead_name: user?.name, lead_avatar: user?.avatar })}
            />
          </div>
          <div className="w-1/5 space-y-1">
            <SelectUser
              placeholder="Account Manager"
              value={{ _id: filters.account_manager_id, name: filters.account_manager_name, avatar: filters.account_manager_avatar }}
              onChange={(user) => setFilters({ ...filters, page: 1, account_manager_id: user?._id, account_manager_name: user?.name, account_manager_avatar: user?.avatar })}
            />
          </div>
          <div className="w-1/5 space-y-1">
            <div className="w-full">
              <MultiSelect
                id="select-status"
                options={["active", "closed"].map((type) => ({ value: type, label: type }))}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, page: 1, status: e.map((e) => e.value) }))}
                placeholder="status"
              />
            </div>
          </div>
        </div>
        <StagePills
          value={Array.isArray(filters.stage) ? filters.stage : filters.stage ? [filters.stage] : []}
          onChange={(stages) => setFilters((f) => ({ ...f, page: 1, stage: stages }))}
        />
        <div className="space-y-6">
          {projects && projects.length > 0 ? (
            <>
              {projects.map((project) => (
                <Project
                  project={project}
                  key={project._id}
                  budgets={budgetsByProject[project._id] || []}
                  onArchive={(id) => setProjects(projects.filter((p) => p._id !== id))}
                />
              ))}
              <div className="flex justify-between items-center mx-[25%]">
                <Pagination page={filters.page} setPage={(page) => setFilters({ ...filters, page })} last={Math.ceil(total / filters.per_page)} />
              </div>
            </>
          ) : (
            <NoProjects />
          )}
        </div>
      </section>
    </div>
  );
}

const NoProjects = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
      <div className="text-center">
        <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">No projects found</h3>
        <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria to find what you're looking for.</p>
      </div>
    </div>
  );
};

const PerPageSelector = ({ value, onChange }) => {
  const options = [5, 10, 25, 50, 100];

  useEffect(() => {
    // Load from localStorage on component mount
    const savedPerPage = localStorage.getItem("projectsPerPage");
    if (savedPerPage && options.includes(Number(savedPerPage))) {
      onChange(Number(savedPerPage));
    }
  }, []);

  const handleChange = (newValue) => {
    // Save to localStorage when changed
    localStorage.setItem("projectsPerPage", newValue);
    onChange(Number(newValue));
  };

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="per-page" className="text-sm text-gray-600">
        Per page:
      </label>
      <select id="per-page" className="border border-gray-300 rounded-md px-2 py-1 text-sm pr-5" value={value} onChange={(e) => handleChange(e.target.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

const STAGE_COLORS = {
  run: "bg-green-100 text-green-800",
  prospection: "bg-yellow-100 text-yellow-800",
  dormant: "bg-gray-100 text-gray-600",
  dead: "bg-red-100 text-red-800",
};

const StageBadge = ({ stage }) => {
  const colors = STAGE_COLORS[stage] || "bg-gray-100 text-gray-600";
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors}`}>{stage}</span>;
};

const STAGES = ["run", "prospection", "dormant", "dead"];

const StagePills = ({ value, onChange }) => {
  const toggle = (stage) => {
    if (value.includes(stage)) onChange(value.filter((s) => s !== stage));
    else onChange([...value, stage]);
  };
  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">Stage:</span>
      <button
        type="button"
        onClick={() => onChange([])}
        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
          value.length === 0 ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
        }`}>
        All
      </button>
      {STAGES.map((stage) => {
        const active = value.includes(stage);
        const baseColors = STAGE_COLORS[stage] || "bg-gray-100 text-gray-600";
        return (
          <button
            key={stage}
            type="button"
            onClick={() => toggle(stage)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition capitalize ${
              active ? `${baseColors} border-current ring-2 ring-offset-1 ring-current` : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}>
            {stage}
          </button>
        );
      })}
    </div>
  );
};

const Project = ({ project, budgets = [], onArchive }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleArchive = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { ok } = await api.put(`/project/${project._id}`, { status: "inactive" });
      if (!ok) throw new Error("Failed to archive project");
      toast.success(`${project.name} archived`);
      setMenuOpen(false);
      if (onArchive) onArchive(project._id);
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive project");
    }
  };

  return (
    <div className="relative grid grid-cols-12 items-center justify-between bg-white rounded-lg border border-gray-200 hover:border-gray-300">
      <Link
        key={project._id}
        to={`/project/${project._id}`}
        onClick={() => {
          addRecentProject(project);
        }}
        className="col-span-11 flex items-center gap-x-6 p-4 cursor-pointer">
        <img className="h-12 w-12 flex-none rounded-md bg-gray-50 object-contain" src={project.logo} alt="project logo" />
        <div className="min-w-0">
          <div className="flex items-center gap-x-3 flex-wrap">
            <p className="text-base font-semibold leading-6 text-gray-900">{project.name}</p>
            {project.organisation_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                {project.organisation_name}
              </span>
            )}
            {project.stage && <StageBadge stage={project.stage} />}
          </div>
          <div className="flex flex-row items-center mt-1 gap-x-4">
            {project.lead_name && (
              <div className="flex items-center gap-x-2">
                {project.lead_avatar && <img src={project.lead_avatar} alt="lead avatar" className="h-6 w-6 rounded-full" />}
                <p className="text-sm text-gray-700">Lead: {project.lead_name}</p>
              </div>
            )}
            {project.account_manager_name && (
              <div className="flex items-center gap-x-2">
                {project.account_manager_avatar && <img src={project.account_manager_avatar} alt="AM avatar" className="h-6 w-6 rounded-full" />}
                <p className="text-sm text-gray-700">AM: {project.account_manager_name}</p>
              </div>
            )}
          </div>
          <div className="hidden sm:block mt-1 leading-5 max-h-60 overflow-y-auto">
            {(project.description ? project.description : "").split("\n").map((line, i) => (
              <p key={i} className="text-sm font-medium text-gray-700">
                {line}
              </p>
            ))}
          </div>
          {budgets.length > 0 && (
            <div className="mt-3 space-y-2">
              {budgets.map((b) => {
                const usedPct = b.amount ? (b.amountUsed / b.amount) * 100 : 0;
                const isOver = usedPct > 100;
                const barColor = isOver ? "bg-red-600" : usedPct >= 80 ? "bg-orange-500" : usedPct >= 60 ? "bg-yellow-500" : "bg-green-500";
                const isSigned = !!b.signed_at;
                const isProspect = project.stage === "prospection";
                const showConfidence = isProspect && !isSigned;
                const confidence = typeof b.confidence === "number" ? b.confidence : 0;
                const confidenceColor = confidence >= 80 ? "bg-green-100 text-green-700" : confidence >= 50 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700";
                return (
                  <div
                    key={b._id}
                    className={`flex items-center gap-3 ${isOver ? "bg-red-50 border border-red-200 rounded-md px-2 py-1 animate-pulse" : ""}`}>
                    <span className="text-xs font-medium truncate w-32 shrink-0 text-gray-700" title={b.name}>{b.name}</span>
                    <span
                      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded shrink-0 inline-flex items-center gap-1 ${
                        isSigned ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                      }`}
                      title={
                        isSigned
                          ? `Budget signed on ${new Date(b.signed_at).toLocaleDateString()} — agreed with the client`
                          : "Budget not signed — proposed/draft, not yet agreed with the client"
                      }>
                      {isSigned ? "✓ Signed" : "✎ Draft"}
                    </span>
                    <div
                      className={`h-2 bg-gray-200 rounded-full overflow-hidden w-40 shrink-0 ${isOver ? "ring-2 ring-red-500" : ""}`}
                      title={`Used ${Math.round(usedPct)}% of budget (${(b.amountUsed || 0).toLocaleString("fr-FR")}€ of ${(b.amount || 0).toLocaleString("fr-FR")}€)`}>
                      <div className={`h-full ${barColor}`} style={{ width: `${Math.max(Math.min(usedPct, 100), usedPct > 0 ? 4 : 0)}%` }} />
                    </div>
                    <span className={`text-xs whitespace-nowrap ${isOver ? "text-red-700 font-semibold" : "text-gray-600"}`} title="Amount spent / total budget">
                      Spent: {(b.amountUsed || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })} /{" "}
                      {(b.amount || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}
                    </span>
                    <span
                      className={`text-xs w-20 text-right whitespace-nowrap ${isOver ? "text-red-700 font-bold" : "text-gray-500"}`}
                      title="Percentage of budget used">
                      {isOver && "⚠ "}{Math.round(usedPct)}% used
                    </span>
                    {showConfidence && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${confidenceColor}`}
                        title="Sales confidence — likelihood this prospect closes (0–100%).">
                        {confidence}% confidence
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Link>

      {/* 3-dot menu */}
      <div className="col-span-1 flex justify-end pr-4" ref={menuRef}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <HiEllipsisVertical className="w-5 h-5 text-gray-500" />
        </button>
        {menuOpen && (
          <div className="absolute right-4 top-12 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/project/${project._id}/edit`);
                setMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              Go to edit
            </button>
            <button onClick={handleArchive} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
              Archive
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function RecentProjects({ onSelect }) {
  const [recents, setRecents] = useState([]);
  const user = useSelector((state) => state.Auth.user);

  useEffect(() => {
    const localRecent = JSON.parse(localStorage.getItem("recentProjects") || "[]");
    setRecents(localRecent);
  }, []);

  if (!recents.length) return null;
  if (user.role === "client") return null;

  return (
    <div className="flex items-center gap-2 mb-4 overflow-x-scroll w-full">
      <span className="text-xs text-gray-500 mr-2">Recent:</span>
      {recents.map((p) => (
        <button
          key={p._id}
          onClick={() => onSelect && onSelect(p)}
          className="flex items-center pl-2 py-1 pr-7 bg-gray-100 hover:bg-gray-200 rounded-full shadow-sm border border-gray-200 whitespace-nowrap">
          <img src={p.logo} alt="logo" className="w-5 h-5 rounded-full object-cover mr-2" />
          <span className="text-xs font-medium text-gray-700 break-words text-left">{p.name}</span>
        </button>
      ))}
    </div>
  );
}
